import { supabase } from '../client';
import { Job, JobStatus } from '@core/types';
import type { StructuredJD } from '@modules/smart-match/types';

export type JobSortColumn = 'title' | 'created_at' | 'status' | 'opened_at' | 'location';

export interface JobListOptions {
  clientId?: string;
  status?: JobStatus;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: JobSortColumn;
  /** When omitted, defaults: created_at → false, others → true */
  sortAsc?: boolean;
}

export interface JobListResult {
  data: Job[];
  total: number;
}

/** JD extraction can return null/empty title; DB requires NOT NULL title. */
function deriveJobTitleFromStructured(structured: StructuredJD): string {
  const raw = typeof structured.title === 'string' ? structured.title.trim() : '';
  if (raw.length > 0) return raw.slice(0, 500);
  const summary =
    typeof structured.description_summary === 'string' ? structured.description_summary.trim() : '';
  if (summary.length > 0) {
    const firstLine =
      summary
        .split(/\r?\n/)
        .map((l) => l.trim())
        .find((l) => l.length > 0) ?? summary;
    return firstLine.length > 200 ? `${firstLine.slice(0, 197)}…` : firstLine;
  }
  return 'Untitled position';
}

export const jobService = {
  async list(
    orgId: string,
    filters?: JobListOptions
  ): Promise<Job[] | JobListResult> {
    const opts = filters ?? {};

    const sortCol: JobSortColumn = opts.sortBy ?? 'created_at';
    const defaultAsc = sortCol === 'created_at' ? false : true;
    const ascending = opts.sortAsc ?? defaultAsc;

    let query = supabase
      .from('ggj_b2b_jobs')
      .select('*, client:ggj_b2b_clients(id, name)', { count: opts.pageSize ? 'exact' : undefined })
      .eq('org_id', orgId)
      .order(sortCol, { ascending, nullsFirst: false });

    if (opts.clientId) query = query.eq('client_id', opts.clientId);
    if (opts.status)   query = query.eq('status', opts.status);
    if (opts.search)   query = query.ilike('title', `%${opts.search}%`);

    if (opts.pageSize != null) {
      const page = Math.max(1, opts.page ?? 1);
      const from = (page - 1) * opts.pageSize;
      const to = from + opts.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    if (error) throw error;

    if (opts.pageSize != null) {
      return { data: (data ?? []) as Job[], total: count ?? 0 };
    }
    return (data ?? []) as Job[];
  },

  async getById(id: string): Promise<Job | null> {
    const { data } = await supabase
      .from('ggj_b2b_jobs')
      .select('*, client:ggj_b2b_clients(id, name, contact_email)')
      .eq('id', id)
      .single();
    return data as Job | null;
  },

  async create(payload: Omit<Job, 'id' | 'created_at' | 'client'>): Promise<Job> {
    // Strip columns that may not yet exist in older DB instances (PGRST204 guard).
    // Run supabase/patch_job_publish_columns.sql to add them.
    const { publish_channels, scheduled_publish_at, scheduled_publish_job_id, ...base } = payload as typeof payload & {
      publish_channels?: unknown;
      scheduled_publish_at?: unknown;
      scheduled_publish_job_id?: unknown;
    };
    void publish_channels; void scheduled_publish_at; void scheduled_publish_job_id;
    const { data, error } = await supabase
      .from('ggj_b2b_jobs')
      .insert(base)
      .select()
      .single();
    if (error) throw error;
    return data as Job;
  },

  async update(id: string, payload: Partial<Job>): Promise<Job> {
    const { data, error } = await supabase
      .from('ggj_b2b_jobs')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: JobStatus): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_jobs')
      .update({ status, ...(status === 'filled' ? { filled_at: new Date().toISOString().split('T')[0] } : {}) })
      .eq('id', id);
    if (error) throw error;
  },

  /**
   * Deletes one job. Dependent applications and other child rows cascade (see migration 003 / related FKs).
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_jobs').delete().eq('id', id);
    if (error) throw error;
  },

  async createFromExtraction(
    orgId: string,
    structured: StructuredJD,
    clientId: string | null
  ): Promise<Job> {
    const remoteMap: Record<string, string> = {
      remote: 'remote',
      hybrid: 'hybrid',
      onsite: 'full_time',
    };

    const title = deriveJobTitleFromStructured(structured);
    const structuredForStorage = { ...structured, title };

    const payload = {
      org_id: orgId,
      client_id: clientId || null,
      title,
      description: structured.description_summary ?? '',
      location: structured.location,
      skills_required: structured.skills_required ?? [],
      employment_type: structured.remote_policy ? (remoteMap[structured.remote_policy] ?? 'full_time') : 'full_time',
      experience_min: structured.experience_min,
      experience_max: structured.experience_max,
      status: 'open' as JobStatus,
      hiring_deadline: structured.hiring_deadline,
      budget_min: structured.budget_min,
      budget_max: structured.budget_max,
      budget_currency: structured.budget_currency || 'INR',
      openings_count: structured.openings_count || 1,
      jd_structured_json: structuredForStorage as unknown as Record<string, unknown>,
      /** NOT NULL on `ggj_b2b_jobs`; explicit null overrides DB default and fails insert */
      publish_status: 'draft',
      published_at: null,
      publish_channels: [],
      scheduled_publish_at: null,
      opened_at: new Date().toISOString().split('T')[0],
      filled_at: null,
      created_by: '',
    };

    return await jobService.create(payload as unknown as Omit<Job, 'id' | 'created_at' | 'client'>);
  },
};
